import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ShieldCheck, IndianRupee, HeartHandshake, ArrowRight } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { SITE, PILLARS } from "../data/siteData";

const pillarIcons = [ShieldCheck, IndianRupee, HeartHandshake];

const TIMELINE = [
  { year: "2006–07", title: "The Beginning", text: "Cytocare Formulations Pvt. Ltd. is founded in Kolkata — the first ever branded oncology molecule pharmaceutical house in Eastern India." },
  { year: "2010s", title: "Oncology Leadership", text: "A trusted name in Oncology, Biosimilars and Injectable Antibiotics with brands like PACLISAFE, COLOPLAT, GEMCE, DOCECARE, PEMSAFE and CYTOCAP." },
  { year: "Expansion", title: "Beyond Oncology", text: "Steady expansion into gastroenterology, neuropsychiatry, antibiotics, cardio-diabetic care, nutraceuticals and supportive therapy — in tie-up with world-class leading manufacturers." },
  { year: "2030", title: "The Vision", text: "To become one of the largest and leading pharmaceutical houses in Eastern India with a pan-India presence — accessible, affordable and ethical in all respect." },
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55 },
};

const About = () => (
  <div data-testid="about-page">
    <PageHeader
      testId="about-header"
      eyebrow="About Cytocare"
      title="A trusted name in oncology, growing into every home"
      subtitle={`Since 2006, ${SITE.name} has been a trusted and prominent name in Oncology, Biosimilars and Injectable Antibiotics — committed to bringing quality medicines at affordable prices.`}
    />

    {/* Story */}
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" data-testid="about-story">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <motion.div {...fadeUp}>
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#1D4ED8]">Who We Are</p>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Eastern India's first branded oncology molecule house
          </h2>
          <p className="mt-5 leading-relaxed text-slate-600">
            Founded in {SITE.founded} and headquartered in Kolkata, Cytocare began with a singular
            conviction — that world-class cancer care should be within everyone's reach. Tied up
            with world-class leading manufacturers, we steadily expanded from oncology into
            gastroenterology, neuropsychiatry, antibiotics, cardio-diabetic care, nutraceuticals
            and supportive therapy.
          </p>
          <p className="mt-4 leading-relaxed text-slate-600">
            Today, we are expanding our horizons from oncology and speciality formulations to
            general pharma — including cardio-diabetic, geriatric care, palliative care and the
            dermatology segment.
          </p>
        </motion.div>
        <motion.div {...fadeUp} className="overflow-hidden rounded-3xl border border-slate-200 shadow-xl">
          <img
            src="https://images.pexels.com/photos/9574558/pexels-photo-9574558.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Cytocare research and quality"
            className="h-full w-full object-cover"
            data-testid="about-story-image"
          />
        </motion.div>
      </div>
    </section>

    {/* Timeline */}
    <section className="bg-[#F2F7FB] py-20" data-testid="about-timeline">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp} className="mb-14">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#1D4ED8]">Our Journey</p>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Two decades of caring</h2>
        </motion.div>
        <div className="space-y-0">
          {TIMELINE.map((t, i) => (
            <motion.div key={t.year} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.08 }} className="relative flex gap-8 pb-12 last:pb-0" data-testid={`timeline-item-${i}`}>
              <div className="flex flex-col items-center">
                <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-4 border-[#1D4ED8] bg-white" />
                {i < TIMELINE.length - 1 && <div className="mt-1 w-px flex-1 bg-slate-300" />}
              </div>
              <div className="pb-2">
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-[#1D4ED8]">{t.year}</p>
                <h3 className="mt-2 text-xl font-bold text-slate-900">{t.title}</h3>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">{t.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Mission & Pillars */}
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" data-testid="about-mission">
      <motion.div {...fadeUp} className="max-w-3xl">
        <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#1D4ED8]">Our Mission</p>
        <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          Quality medicines at affordable prices
        </h2>
        <p className="mt-5 leading-relaxed text-slate-600">
          Our mission is to create a more accessible and affordable pharmaceutical house within
          2030, with value and ethics in all respect — built on three core pillars.
        </p>
      </motion.div>
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {PILLARS.map((p, i) => {
          const Icon = pillarIcons[i];
          return (
            <motion.div
              key={p.title}
              {...fadeUp}
              transition={{ duration: 0.55, delay: i * 0.1 }}
              className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm transition-shadow duration-300 hover:shadow-xl"
              data-testid={`about-pillar-${i}`}
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-[#1D4ED8]">
                <Icon size={22} />
              </div>
              <h3 className="mt-6 text-xl font-bold text-slate-900">{p.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{p.desc}</p>
            </motion.div>
          );
        })}
      </div>
    </section>

    {/* HQ spotlight */}
    <section className="bg-[#0A1633] py-20 text-white" data-testid="about-hq">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-xl">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-blue-300">Headquartered in Kolkata</p>
          <h2 className="mt-3 text-2xl font-extrabold sm:text-3xl">Talk to our team</h2>
          <p className="mt-4 text-slate-300" data-testid="about-hq-address">
            Write to us at <span className="font-semibold text-white">{SITE.email}</span> — we typically respond within one working day.
          </p>
        </div>
        <Link
          to="/contact"
          data-testid="about-contact-button"
          className="inline-flex items-center gap-2 rounded-full bg-[#1D4ED8] px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#3B82F6]"
        >
          Get in touch <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  </div>
);

export default About;
